import { useMutation, useQuery } from '@apollo/client'
import cloneDeep from 'lodash/cloneDeep'
import set from 'lodash/set'

import {
  ArtistNotificationSettings,
  ArtistNotificationSettingsQuery,
  ArtistNotificationSettingsQueryVariables,
  EmailNotificationSettings,
  EmailNotificationSettingsQuery,
  EmailNotificationSettingsQueryVariables,
  UpdateArtistNotificationSettings,
  UpdateArtistNotificationSettingsMutation,
  UpdateArtistNotificationSettingsMutationVariables,
  UpdateUserNotificationSettings,
  UpdateUserNotificationSettingsMutation,
  UpdateUserNotificationSettingsMutationVariables,
} from '@/clients/utoniq-core/schema'
import { config } from '@/config'
import { useMessage } from '@/core'
import { makeViewModel } from '@/core/architecture'
import { useCurrentUser } from '@/core/auth'

import { Action, State } from './template'

export const useViewModel = makeViewModel<State, Action>(() => {
  const { userClaims } = useCurrentUser()

  const isArtist = !!userClaims?.orgs?.[config.organizationId]?.artist

  const { showError } = useMessage()
  const { data } = useQuery<
    EmailNotificationSettingsQuery,
    EmailNotificationSettingsQueryVariables
  >(EmailNotificationSettings)

  const { data: artistData } = useQuery<
    ArtistNotificationSettingsQuery,
    ArtistNotificationSettingsQueryVariables
  >(ArtistNotificationSettings, { skip: !isArtist })

  const [updateUserNotificationSettings] = useMutation<
    UpdateUserNotificationSettingsMutation,
    UpdateUserNotificationSettingsMutationVariables
  >(UpdateUserNotificationSettings)

  const [updateArtistNotificationSettings] = useMutation<
    UpdateArtistNotificationSettingsMutation,
    UpdateArtistNotificationSettingsMutationVariables
  >(UpdateArtistNotificationSettings)

  const notifications = data?.user?.settings?.notifications
  const artistNotifications = artistData?.artist?.settings?.notifications

  return {
    state: {
      isArtist,
      newPost: !!notifications?.post?.newPost?.push,
      replyPost: !!notifications?.post?.replyPost?.push,
      newMission: !!notifications?.mission?.newMission?.push,
      replyCommentMission: !!notifications?.mission?.replyCommentMission?.push,
      requestTrade: !!notifications?.trade?.requestTrade?.push,
      commentTrade: !!notifications?.trade?.commentTrade?.push,
      agreedTrade: !!notifications?.trade?.agreedTrade?.push,
      declinedTrade: !!notifications?.trade?.declinedTrade?.push,
      startCampaign: !!notifications?.campaign?.startCampaign?.push,
      endCampaign: !!notifications?.campaign?.endCampaign?.push,
      startLiveStream: !!notifications?.liveStream?.startLiveStream?.push,
      artistReplyPost: !!artistNotifications?.post?.replyPost?.push,
      artistNewThread:
        !!artistNotifications?.artistThreadChannel?.newThread?.push,
      artistMembershipSubscribe:
        !!artistNotifications?.membership?.subscribe?.push,
      announcementOfficial: !!notifications?.announcement?.official?.push,
      commentThread: !!notifications?.threadChannel?.commentThread?.push,
      replyCommentThread:
        !!notifications?.threadChannel?.replyCommentThread?.push,
      gachaUpcomingExpiry: !!notifications?.gacha?.upcomingExpiry?.push,
    },
    actions: {
      async onToggle(value) {
        await updateUserNotificationSettings({
          variables: { updateUserNotificationSettingsInputs: value },
          optimisticResponse() {
            const updateUserNotificationSettings = {
              id: data?.user?.id ?? '',
              settings: {
                notifications: cloneDeep(notifications),
                __typename: 'UserSettings',
              },
              __typename: 'User',
            }
            const [type, subType] = value.type.split('_')
            set(
              updateUserNotificationSettings,
              `settings.notifications.${type}.${subType}.${value.channel}`,
              value.flag
            )
            return { updateUserNotificationSettings }
          },
        }).catch(showError)
      },
      async onArtistToggle(value) {
        await updateArtistNotificationSettings({
          variables: { inputs: value },
          optimisticResponse() {
            const updateArtistNotificationSettings = {
              id: artistData?.artist?.id ?? '',
              settings: {
                notifications: cloneDeep(artistNotifications),
                __typename: 'ArtistSettings',
              },
              __typename: 'Artist',
            }
            const [type, subType] = value.type.split('_')
            set(
              updateArtistNotificationSettings,
              `settings.notifications.${type}.${subType}.${value.channel}`,
              value.flag
            )
            return { updateArtistNotificationSettings }
          },
        }).catch(showError)
      },
    },
  }
})
